import { type FC, useCallback } from "react"
import styled from "styled-components"

import { type CoursePageInputType, type IntendedPageType } from "@forento/core/models/course"
import { type VideoCaptionLanguageCode } from "@forento/core/models/video"
import { exhaustiveGuard } from "@forento/core/utilities/switch"

import { DefaultModal } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { query } from "~/utilities/trpc"

import AudioContent from "./AudioContent"
import CustomContent from "./CustomContent"
import IframeContent from "./IframeContent"
import InputContent from "./InputContent"
import LoomContent from "./LoomContent"
import QuizContent from "./QuizContent"
import TextContent from "./TextContent"
import VideoContent from "./VideoContent"
import VimeoContent from "./VimeoContent"
import YouTubeContent from "./YouTubeContent"

type Props = {
	isOpen: boolean
	type: IntendedPageType
	iframeAllowedDomains: string[]
	page: {
		id: number
		title: string
		text: string | null
		audioUrl: string | null
		image: { filePath: string; contain: boolean } | null
		quiz: { answers: { text: string; isCorrect: boolean }[] | null } | null
		video:
			| { host: "apiVideo"; apiVideoId: string }
			| { host: "bunny"; bunnyVideoId: string; thumbnailUrl: string | null; captions: VideoCaptionLanguageCode[] }
			| null
		youTubeVideoId: string | null
		vimeoVideoId: string | null
		loomVideoId: string | null
		iframeUrl: string | null
		inputType: CoursePageInputType | null
		attachments: {
			id: number
			fileType: string
			label: string
			url: string
		}[]
		links: {
			id: number
			label: string
			url: string
		}[]
	}
	onClose(args?: { reloadCourse?: boolean }): Promise<void>
}
const EditPageModal: FC<Props> = ({ isOpen, type, iframeAllowedDomains, page, onClose }) => {
	const alert = useAlert()

	const update = query.course.updatePage.useMutation({
		async onSuccess() {
			await onClose({ reloadCourse: true })
		},
		async onError() {
			await alert.show("Error", "Failed to save page. Please try again.")
		},
	})

	const remove = query.course.deletePage.useMutation({
		async onSuccess() {
			await onClose({ reloadCourse: true })
		},
		async onError() {
			await alert.show("Error", "Failed to delete page. Please try again.")
		},
	})

	const handleSavePage = useCallback(
		(data: {
			title: string
			text?: string | null
			youTubeVideoId?: string
			vimeoVideoId?: string
			loomVideoId?: string
			iframeUrl?: string
			allowIframeDomain?: boolean
			thumbnailDataUrl?: string | null
			audioDataUrl?: string | null
			imageDataUrl?: string | null
			containImage?: boolean
			removeCaptions?: VideoCaptionLanguageCode[]
			addCaptions?: { language: VideoCaptionLanguageCode; base64: string }[]
		}) => {
			update.mutate({ id: page.id, data })
		},
		[page.id, update],
	)

	const handleDeletePage = useCallback(() => {
		remove.mutate(page.id)
	}, [page.id, remove])

	return (
		<DefaultModal isOpen={isOpen} width={600}>
			<Header>Edit page</Header>
			<Content>
				{type === "video" ? (
					<VideoContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "youtube" ? (
					<YouTubeContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "vimeo" ? (
					<VimeoContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "loom" ? (
					<LoomContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "audio" ? (
					<AudioContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "iframe" ? (
					<IframeContent
						key={page.id}
						page={page}
						iframeAllowedDomains={iframeAllowedDomains}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "text" ? (
					<TextContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "input" ? (
					<InputContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "quiz" ? (
					<QuizContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : type === "custom" ? (
					<CustomContent
						key={page.id}
						page={page}
						isSaving={update.isPending}
						isDeleting={remove.isPending}
						onClose={onClose}
						onSavePage={handleSavePage}
						onDeletePage={handleDeletePage}
					/>
				) : (
					exhaustiveGuard(type)
				)}
			</Content>
		</DefaultModal>
	)
}

const Header = styled.h2`
	font-weight: 600;
	font-size: 18px;
	color: #101828;
	margin: 0 24px;
`

const Content = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin: 24px 24px 0;
`

export default EditPageModal
